import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import styles from "../GlobalSearch.styles";

import { selectSearch } from "../selectors";
import DefaultModal from "../../../common/components/modal/default-modal/DefaultModal";
import { types } from "../../../common/components/helpers/eventEmitter";
import ModalHeader from "../../../common/components/modal/modal-header/ModalHeader";
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Container,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ModalFooter from "../../../common/components/modal/modal-footer/ModalFooter";
import { modalTypes } from "../../../common/constants/field-constants";
import BlueSwitch from "../../../common/components/styled-components/Blueswitch";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import StyledSearch from "../../../common/components/styled-components/StyledSearch";
import StyledSearchIconWrapper from "../../../common/components/styled-components/StyledSearchIconWrapper";
import StyledInputBase from "../../../common/components/styled-components/StyledInputBase";
import { findObjectByFiltersRequest } from "../actions/search";
import AlertService from "../../../common/services/notification.service";
import { updateEntityRequest } from "../../leadgen/actions/entityDetails";
import { RootState } from '../../../reducers';

type EntityId = string;
type Filters = {
  byKadId: boolean;
};
type SearchObject = {
  object_id: string;
  objectAddress?: string | null;
  city?: string | null;
  kadaster_id?: string | null;
  [key: string]: unknown;
}

const ObjectSearchModal = (props: any) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [entityId, setEntityId] = useState<EntityId | null>(null);
  const [options, setOptions] = useState<SearchObject[]>([]);
  const [inputValue, setInputValue] = useState("");
  // const [value, setValue] = useState(null);
  const [limitObjects, setLimitObjects] = useState<number>(5);
  const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
  //TODO check and update

  const [filters, setFilters] = useState<Filters>({
    byKadId: false,
  }); /** will be used later **/
  const [objectsList, setObjectList] = useState<SearchObject[]>([]);
  const { localSearchObjectResult, processing } = props;

  const handleOpen = (id: EntityId) => {
    setEntityId(id);
    setFilters((prev) => ({...prev, byKadId: false}));
  };

  const increment = () => {
    setLimitObjects((prev) => Math.min(prev + 5, 50) );
  };

  const decrement = () => {
    setLimitObjects((prev) => Math.max(prev - 5, 5) );
  }

  useEffect(() => {
    if (inputValue === "") {
      setOptions([]);
      return;
    }
    const newOptions: SearchObject[] = localSearchObjectResult ?
        [...localSearchObjectResult] : [];
    setOptions(newOptions);

  }, [localSearchObjectResult, inputValue]);

  const handleChecked = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setFilters((prev) => ({
      ...prev,
      byKadId: checked,
    }) )
  }

  const handleChange = (
    _event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    setInputValue(newInputValue);
    if (newInputValue.length >= 2) {
      const requestData = {
        ...filters,
        inputValue: newInputValue,
        limit: limitObjects,
      };
      dispatch(findObjectByFiltersRequest(requestData));
    }
  };

  const handleChangeList = (object: SearchObject | null) => {

    if (!object) return;
    setObjectList((prev) => {
      if (prev.some((currentObject) => currentObject?.object_id === object?.object_id )) {
        AlertService.warning("This object is already in list");
        return prev;
      }
      return [...prev, object];
    })
  };

  const handleDeleteSelectedObject = (object_id: SearchObject["object_id"]) => {
    setObjectList((prev) => prev.filter(
      (object) => object.object_id !== object_id
    ))
  };

  const handleClear = () => {
    setClearTooltipOpen(false);
    setInputValue("");
    setOpen(false);
  };
  const handleClearAll = () => {
    handleClear();
    setObjectList([]);
    setLimitObjects(5);
    setOptions([]);
  }

  const handleAssignObjectsToEntity = (closeModal: () => void) => {
    const actionTypes = ["objects"];
    const newLinkedObjectIds = objectsList.map((object) => object.object_id);
    let data = {
      newLinkedObjectIds,
    };
    dispatch(updateEntityRequest(false, actionTypes, entityId, data));
    setOpen(false);
    setEntityId(null);
    setObjectList([]);
    setLimitObjects(5);
    closeModal();
  };

  return (
    <>
      <DefaultModal onOpen={handleOpen} nameEvent={types.openObjectSearchModal}>
        {({ closeModal }) => (
          <>
            <ModalHeader title="Objects search" />
            <Container>
              <Box sx={{ marginBottom: "20px", marginTop: "5px" }}>
                <Box sx={styles.filters}>
                  <FormControlLabel
                    value="end"
                    control={
                    <BlueSwitch
                      onChange={handleChecked}
                      checked={filters.byKadId}
                    />}
                    id="search-by-kad-id"
                    label="Search by kad ID"
                    labelPlacement="end"
                    style={{ color: "#495057" }}

                  />
                  <Box sx={styles.filters}>
                    <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                      SHOW:
                    </Typography>
                    <ButtonGroup variant="text" aria-label="Basic button group">
                      <Button
                        sx={styles.splitButton}
                        onClick={increment}
                      >+{limitObjects}</Button>
                      <Button
                        sx={styles.splitButton}
                        onClick={decrement}
                      >-5</Button>
                    </ButtonGroup>
                  </Box>
                </Box>
                <Autocomplete
                  open={open && inputValue.length >= 2}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setOpen(false);
                  }}
                  options={options}
                  loading={processing}
                  filterOptions={(x) => x}
                  autoComplete
                  includeInputInList
                  filterSelectedOptions
                  value={null}
                  blurOnSelect
                  fullWidth
                  getOptionLabel={(option) =>
                    `${option.objectAddress?.includes("null") ? "n/a" : option.objectAddress}, ${option.city}`
                  }
                  onChange={(_event, newValue) => {
                    setOptions((prev) => (newValue ? [newValue, ...prev] : prev));
                    handleChangeList(newValue);
                    setInputValue("");
                    setOpen(false);
                  }}
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={`${option.id || Math.random()}`}>
                        <Grid container alignItems="center">
                          <Grid sx={{ display: "flex" }}>
                            <ApartmentIcon sx={styles.resultIcon} />
                            <Grid>
                              <Typography>
                                {`${option.objectAddress?.includes("null") ? "n/a" : option.objectAddress}, ${option.city}`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                      </li>
                    );
                  }}
                  inputValue={inputValue}
                  // onInputChange={(_event, newInputValue) =>
                  //   handleChange(newInputValue)
                  // }
                  onInputChange={handleChange}

                  renderInput={(params) => {
                    const {InputLabelProps, InputProps, ...rest} = params;
                    return (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <StyledSearch ref={params.InputProps.ref}>
                        <StyledSearchIconWrapper>
                          <SearchIcon sx={{ color: "#495057" }} />
                        </StyledSearchIconWrapper>
                        <StyledInputBase
                          {...rest}
                          inputProps={{
                            ...rest.inputProps,
                            placeholder: "Search",
                          }}
                          sx={{
                            "&.MuiInputBase-input": {paddingRight: "36px"},
                          }}
                        />
                        <Tooltip
                          title="Clear filed"
                          open={clearTooltipOpen && inputValue.length > 0}
                          onOpen={() => setClearTooltipOpen(true)}
                          onClose={() => setClearTooltipOpen(false)}
                        >
                        <span>
                          <IconButton
                            onClick={handleClear}
                            sx={{
                              position: "absolute",
                              right: 0,
                              bottom: 2,
                              opacity: inputValue.length > 0 ? 1 : 0,
                              pointerEvents: inputValue.length > 0 ? "auto" : "none",
                            }}
                            size="small"
                          >
                              <ClearIcon
                                sx={{ color: "#495057" }}
                                fontSize="small"
                              />
                          </IconButton>
                        </span>
                        </Tooltip>
                      </StyledSearch>
                    </Box>
                  )}
                }
                />
                <List>
                  {objectsList.length > 0 &&
                    objectsList.map((object, index) => (
                      <ListItem key={index}>
                        <ListItemText>
                          {object.objectAddress?.includes("null") ? `N/a, Kad. ID ${object.kadaster_id}`: object.objectAddress}
                        </ListItemText>
                        <IconButton
                          onClick={() =>
                            handleDeleteSelectedObject(object.object_id)
                          }
                          size="small"
                        >
                          <ClearIcon
                            sx={{ color: "#495057" }}
                            fontSize="small"
                          />
                        </IconButton>
                      </ListItem>
                    ))}
                </List>
              </Box>
            </Container>
            <ModalFooter
              closeModal={closeModal}
              handleClear={handleClearAll}
              disabled={objectsList.length === 0}
              type={modalTypes.SAVE}
              onSave={() => handleAssignObjectsToEntity(closeModal)}
            />
          </>
        )}
      </DefaultModal>
    </>
  );
};

function mapStateToProps(state: RootState) {
  // @ts-ignore
  const { localSearchObjectResult, processing } = selectSearch(state);
  return { localSearchObjectResult, processing };
}

export default connect(mapStateToProps)(ObjectSearchModal);
